import { useState, useEffect } from 'react';
import { AnimMagicUIBorderBeam } from '@readdy/anim/card/magic-ui-border-beam/react';

// カード半径を全階層で一致させる（Border Beam のビーム走行パスをカードの角丸に合わせるため）
const CARD_RADIUS = '1.5rem'; // = rounded-3xl (24px)

export default function Plans() {
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mql.matches);
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // カードの中身（画像＋料金＋特徴）。静的版・アニメ版で共通利用
  const cardBody = (
    <>
      <div className="relative h-56 md:h-64 rounded-t-3xl overflow-hidden">
        <img
          src="https://storage.readdy-site.link/project_files/1655f95b-cd6b-479d-8503-b76e04453858/fea33dab-1610-471b-948b-c55d0b61a5d7_compressed_5A5D53FB-9A6C-42A6-85B9-DF704AC5F5B7.webp"
          alt="アネットパーキング 一律料金"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <span className="absolute top-3 left-3 bg-primary-500 text-background-50 text-[10px] tracking-wider uppercase rounded-full px-3 py-1">
          送迎込み
        </span>
      </div>
      <div className="p-6 md:p-8 text-center">
        <div className="text-foreground-600 text-xs uppercase tracking-widest">
          全車種一律
        </div>
        <div className="mt-3 flex items-baseline justify-center gap-1">
          <span className="font-heading text-5xl md:text-6xl text-foreground-950">
            ¥3,000
          </span>
          <span className="text-foreground-600 text-sm">／税込</span>
        </div>
        <p className="mt-4 text-foreground-700 text-sm leading-relaxed">
          軽自動車・普通車・ミニバン・大型車まで、車種に関わらず同一料金。
          スタジアムまでの往復無料送迎付き。
        </p>

        <div className="mt-6 grid grid-cols-2 gap-3 text-left">
          {[
            { icon: 'ri-bus-2-line', label: '往復無料送迎' },
            { icon: 'ri-calendar-check-line', label: '当日予約OK' },
            { icon: 'ri-bank-card-line', label: 'カード決済可' },
            { icon: 'ri-smartphone-line', label: 'LINE予約対応' },
          ].map((f) => (
            <div key={f.label} className="flex items-center gap-2 text-xs text-foreground-700">
              <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
                <i className={f.icon} />
              </span>
              {f.label}
            </div>
          ))}
        </div>

        <div className="mt-8 pt-5 border-t border-foreground-950/10">
          <div className="text-xs text-foreground-600 mb-3">
            お支払い方法
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3 text-xs text-foreground-700">
            <span className="bg-background-100 rounded-full px-3 py-1.5">現金</span>
            <span className="bg-background-100 rounded-full px-3 py-1.5">各種カード</span>
            <span className="bg-background-100 rounded-full px-3 py-1.5">ペイペイ</span>
            <span className="bg-background-100 rounded-full px-3 py-1.5">dカード</span>
          </div>
        </div>

        <a
          href="#reserve"
          className="mt-8 inline-flex items-center justify-center gap-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full px-8 py-3.5 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors w-full"
        >
          このプランで予約する
          <span className="w-7 h-7 flex items-center justify-center rounded-full bg-background-50 text-primary-600">
            <i className="ri-arrow-right-line text-sm" />
          </span>
        </a>
      </div>
    </>
  );

  // 静的版（reduced-motion 時）：リング付きのシンプルなカード
  const staticCard = (
    <div className="bg-background-50 rounded-3xl overflow-hidden ring-2 ring-primary-500">
      {cardBody}
    </div>
  );

  // アニメ版：Border Beam（縁を光が一周）のみ
  // ポイント：各階層に明示的な border-radius を効かせ、ビームの走行パスをカードの角丸に一致させる
  const animatedCard = (
    <div className="relative" style={{ borderRadius: CARD_RADIUS }}>
      <AnimMagicUIBorderBeam duration={4000} borderWidth={2.5} glowOpacity={0.85}>
        <div className="relative" style={{ borderRadius: CARD_RADIUS }}>
          <div
            className="bg-background-50 overflow-hidden"
            style={{ borderRadius: CARD_RADIUS }}
          >
            {cardBody}
          </div>
        </div>
      </AnimMagicUIBorderBeam>
    </div>
  );

  return (
    <section id="plan" className="bg-background-100 py-24 md:py-32 px-6 md:px-10">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
          <div>
            <span className="font-heading italic text-primary-600 text-sm tracking-[0.3em] uppercase">
              Simple Pricing
            </span>
            <h2 className="mt-4 font-heading text-2xl md:text-4xl lg:text-5xl leading-tight text-foreground-950">
              駐車料金は、<br /><span className="italic">わかりやすく</span><span className="text-primary-600">一律</span>。
            </h2>
          </div>
          <div className="md:text-right">
            <p className="text-foreground-700 text-sm leading-relaxed max-w-md md:ml-auto">
              車種を問わず、試合日1回一律料金。送迎込みの明朗会計です。
              午前入庫〜試合終了後の退出まで、追加料金は発生しません。
            </p>
          </div>
        </div>

        <div className="mt-14 md:mt-20 max-w-md mx-auto">
          {reducedMotion ? staticCard : animatedCard}
        </div>
      </div>
    </section>
  );
}